import React, { useState } from 'react';
import {
  Card, Table, Tag, Button, Modal, Form, Input, Space, message, Statistic, Row, Col,
  DatePicker, Switch, Tooltip
} from 'antd';
import {
  GlobalOutlined, PlusOutlined, DeleteOutlined, ReloadOutlined, EditOutlined,
  WarningOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useListDominiosQuery, useAddDominioMutation, useEditDominioMutation,
  useDeleteDominioMutation, useCheckAllDominiosMutation, useCheckDominioMutation
} from '../store/api';

const MonitorDominios = () => {
  const { data = [], isLoading, refetch } = useListDominiosQuery();
  const [addDominio] = useAddDominioMutation();
  const [editDominio] = useEditDominioMutation();
  const [deleteDominio] = useDeleteDominioMutation();
  const [checkAll, { isLoading: checking }] = useCheckAllDominiosMutation();
  const [checkOne] = useCheckDominioMutation();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const porVencer = data.filter(d => d.dias_restantes !== null && d.dias_restantes >= 0 && d.dias_restantes <= 30).length;
  const vencidos = data.filter(d => d.dias_restantes !== null && d.dias_restantes < 0).length;

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values };
      if (payload.fecha_vencimiento) payload.fecha_vencimiento = payload.fecha_vencimiento.format('YYYY-MM-DD');
      if (editing) {
        await editDominio({ id: editing.id, ...payload }).unwrap();
        message.success('Dominio actualizado');
      } else {
        await addDominio(payload).unwrap();
        message.success('Dominio agregado');
      }
      setModal(false); setEditing(null); form.resetFields(); refetch();
    } catch { message.error('Error al guardar'); }
  };

  const openEdit = (r) => {
    setEditing(r);
    form.setFieldsValue({
      dominio: r.dominio,
      registrador: r.registrador,
      fecha_vencimiento: r.fecha_vencimiento ? dayjs(r.fecha_vencimiento) : null,
      servidores: r.servidores,
      subdominios: r.subdominios,
      activo: r.activo == 1,
    });
    setModal(true);
  };

  const columns = [
    { title: 'Dominio', dataIndex: 'dominio', render: d => <strong>{d}</strong> },
    { title: 'Vence', dataIndex: 'fecha_vencimiento', sorter: (a, b) => dayjs(a.fecha_vencimiento).unix() - dayjs(b.fecha_vencimiento).unix(),
      render: d => d ? dayjs(d).format('DD/MM/YYYY') : '-' },
    { title: 'Días restantes', dataIndex: 'dias_restantes', sorter: (a, b) => (a.dias_restantes ?? 9999) - (b.dias_restantes ?? 9999),
      render: d => {
        if (d === null || d === undefined) return <span style={{ color: '#bbb' }}>Sin fecha</span>;
        if (d < 0) return <Tag color="red">{Math.abs(d)} días vencido</Tag>;
        const color = d > 30 ? '#52c41a' : '#faad14';
        return <span style={{ color, fontWeight: 600 }}>{d} días</span>;
      }},
    { title: 'Registrador', dataIndex: 'registrador', render: r => r || '-' },
    { title: 'Servidores', dataIndex: 'servidores', ellipsis: true, render: s => s ? s.split(',').map((ip, i) => <Tag key={i} color="geekblue">{ip.trim()}</Tag>) : '-' },
    { title: 'Subdominios', dataIndex: 'subdominios', ellipsis: true, render: s => s ? <Tooltip title={s}><span>{s}</span></Tooltip> : '-' },
    { title: 'Último check', dataIndex: 'ultimo_check', render: d => d ? dayjs(d).format('DD/MM HH:mm') : '-' },
    { title: 'Activo', dataIndex: 'activo', width: 80, render: a => <Tag color={a == 1 ? 'green' : 'default'}>{a == 1 ? 'Sí' : 'No'}</Tag> },
    { title: '', key: 'act', width: 120, render: (_, r) => (
      <Space size={4}>
        <Button size="small" icon={<ReloadOutlined />} onClick={async () => { await checkOne(r.id).unwrap(); message.success('Verificado'); refetch(); }} />
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => Modal.confirm({
          title: '¿Eliminar dominio?', content: r.dominio,
          onOk: async () => { await deleteDominio(r.id).unwrap(); message.success('Eliminado'); refetch(); }
        })} />
      </Space>
    )},
  ];

  return (
    <div className="page-container fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title"><GlobalOutlined style={{ marginRight: 8 }} />Vencimiento de Dominios</h1>
          <div className="page-subtitle">{data.length} dominios monitoreados</div>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} loading={checking} onClick={async () => {
            await checkAll().unwrap(); message.success('Verificación completada'); refetch();
          }}>Verificar Todos</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.resetFields(); form.setFieldsValue({ activo: true }); setModal(true); }}>Agregar Dominio</Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={8}><Card size="small"><Statistic title="Total" value={data.length} prefix={<GlobalOutlined />} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Por vencer (≤30 días)" value={porVencer} valueStyle={{ color: '#faad14' }} prefix={<WarningOutlined />} /></Card></Col>
        <Col xs={8}><Card size="small"><Statistic title="Vencidos" value={vencidos} valueStyle={{ color: '#f5222d' }} prefix={<CloseCircleOutlined />} /></Card></Col>
      </Row>

      <Card bordered={false} bodyStyle={{ padding: 0 }}>
        <Table columns={columns} dataSource={data} rowKey="id" loading={isLoading} pagination={{ pageSize: 15 }} size="middle" scroll={{ x: true }} />
      </Card>

      <Modal title={editing ? 'Editar Dominio' : 'Agregar Dominio'} open={modal} onOk={handleSave}
        onCancel={() => { setModal(false); setEditing(null); form.resetFields(); }} okText="Guardar" width={560}>
        <Form form={form} layout="vertical">
          <Form.Item name="dominio" label="Dominio" rules={[{ required: true, message: 'Ingrese el dominio' }]}>
            <Input placeholder="ejemplo.com" disabled={!!editing} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Form.Item name="registrador" label="Registrador">
              <Input placeholder="Ej: GoDaddy, eNom" />
            </Form.Item>
            <Form.Item name="fecha_vencimiento" label="Fecha de vencimiento" rules={[{ required: true, message: 'Seleccione la fecha' }]}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </div>
          <Form.Item name="servidores" label="Servidores asociados (IPs separadas por coma)">
            <Input placeholder="173.212.246.155, 208.87.134.70" />
          </Form.Item>
          <Form.Item name="subdominios" label="Subdominios (opcional)">
            <Input.TextArea rows={2} placeholder="ia, corporativo, api..." />
          </Form.Item>
          <Form.Item name="activo" label="Activo" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MonitorDominios;
